import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import LeaseForm, { LeaseFormMode } from "../components/LeaseForm";
import useLeases from "../hooks/useLeases";
import { useToast } from "@/hooks/use-toast";
import { type LeaseWithRelations } from "../types/lease";
import { type LeaseFormValues } from "../types/lease";
import { UpdateLeaseDto } from "@/common/api/datasources/Api";

export const LeaseDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { leaseUUID } = useParams<{ leaseUUID: string }>();
  const { getLease, updateLease } = useLeases();
  const { toast } = useToast();
  const [lastSubmittedData, setLastSubmittedData] = useState<LeaseFormValues | null>(null);

  const { data: lease, isError, isLoading } = getLease(leaseUUID || "") as {
    data: LeaseWithRelations | undefined;
    isLoading: boolean;
    isError: boolean;
  };

  console.log(lease);

  const formattedLease = lastSubmittedData || (lease ? {
    propertyUUID: lease.propertyUUID,
    tenantUUID: lease.tenant?.userUUID || lease.tenantUUID || "",
    managerUUID: lease.manager?.userUUID || lease.managerUUID || "",
    startDate: lease.startDate ? new Date(lease.startDate) : null,
    endDate: lease.endDate ? new Date(lease.endDate) : null,
    monthlyRent: lease.monthlyRent,
    securityDeposit: lease.securityDeposit ?? 0,
    status: lease.status,
    paymentDay: lease.paymentDay || 1,
    autoRenew: lease.autoRenew,
  } : undefined);

  useEffect(() => {
    if (!lastSubmittedData && lease) {
      setLastSubmittedData(null);
    }
  }, [lease, lastSubmittedData]);

  const handleSubmit = async (data: LeaseFormValues) => {
    console.log("Submitting form data:", data);

    try {
      if (!leaseUUID) return;

      const updateData: UpdateLeaseDto = {
        propertyUUID: data.propertyUUID,
        tenantUUID: data.tenantUUID,
        managerUUID: data.managerUUID,
        startDate: data.startDate ? data.startDate.toISOString().split("T")[0] : "",
        endDate: data.endDate ? data.endDate.toISOString().split("T")[0] : undefined,
        monthlyRent: data.monthlyRent,
        securityDeposit: data.securityDeposit,
        status: data.status,
        paymentDay: data.paymentDay,
        autoRenew: data.autoRenew,
      };

      console.log("Sending update data to API:", updateData);

      const response = await updateLease({
        uuid: leaseUUID,
        data: updateData,
      });

      console.log("API response:", response);

      setLastSubmittedData(data);

      toast({
        title: t("common.success"),
        description: t("leases.update.success"),
      });
    } catch (error) {
      console.error("Failed to update lease:", error);

      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("leases.update.error"),
      });
    }
  };

  if (isError) {
    return <div>{t("common.error")}</div>;
  }

  return (
    <LeaseForm
      initialMode={LeaseFormMode.VIEW}
      onSubmit={handleSubmit}
      initialData={formattedLease}
      isLoading={isLoading}
      onBack={() => navigate(-1)}
    />
  );
};
